/**
 * Maps the `page_ui` tool's input (as declared server-side in
 * `apps/api/src/assistant/page-ui-tool.ts`) onto a `@runcofactor/guide`
 * `GuideOp`.
 *
 * Kept in step with `PageUiInputSchema` BY HAND, same as that file keeps
 * itself in step with `GuideOp` by hand — see its comment for why importing
 * a shared schema doesn't help here either. The tool call arrives over the
 * wire as `unknown` (the assistant's `AssistantUIMessage` type does not pin
 * down a `page_ui`-specific tool shape), so this is also the runtime
 * validation boundary: a drifted or malformed field throws here rather than
 * reaching `guide.execute` with a garbage op.
 */

import { renderObservation, type GuideOp, type GuideOpResult } from '@runcofactor/guide';

type RawPageUiInput = {
  action?: unknown;
  surfaceId?: unknown;
  text?: unknown;
  clearFirst?: unknown;
  key?: unknown;
  waitForText?: unknown;
  gone?: unknown;
};

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

export function toGuideOp(rawInput: unknown): GuideOp {
  if (typeof rawInput !== 'object' || rawInput === null) {
    throw new Error('page_ui call arrived with no input');
  }
  const input = rawInput as RawPageUiInput;

  switch (input.action) {
    case 'snapshot':
      return { action: 'snapshot' };

    case 'read_text': {
      if (!isString(input.surfaceId)) throw new Error('page_ui read_text requires a surfaceId');
      return { action: 'read_text', surfaceId: input.surfaceId };
    }

    case 'click': {
      if (!isString(input.surfaceId)) throw new Error('page_ui click requires a surfaceId');
      return { action: 'click', surfaceId: input.surfaceId };
    }

    case 'type': {
      if (!isString(input.surfaceId)) throw new Error('page_ui type requires a surfaceId');
      if (!isString(input.text)) throw new Error('page_ui type requires text');
      return {
        action: 'type',
        surfaceId: input.surfaceId,
        text: input.text,
        clearFirst: isBoolean(input.clearFirst) ? input.clearFirst : undefined,
      };
    }

    case 'press_key': {
      if (!isString(input.key)) throw new Error('page_ui press_key requires a key');
      return { action: 'press_key', key: input.key };
    }

    case 'wait_for': {
      return {
        action: 'wait_for',
        surfaceId: isString(input.surfaceId) ? input.surfaceId : undefined,
        text: isString(input.waitForText) ? input.waitForText : undefined,
        gone: isBoolean(input.gone) ? input.gone : undefined,
      };
    }

    default:
      throw new Error(`page_ui call had an unrecognized action: ${String(input.action)}`);
  }
}

/**
 * What the model actually receives back from a `page_ui` call.
 *
 * Compact text, not the raw result object. This payload is re-read on every
 * round trip, and the JSON form repeats every key on every row — measured at
 * roughly three times the size on a page with a list.
 *
 * A refusal is prepended rather than replacing the observation: the agent needs
 * to know both that it was refused AND what the page looks like now, which is
 * usually how it works out that a dialog opened over its target.
 */
export function toToolOutput(result: GuideOpResult): string {
  const rendered = renderObservation(result.observed);
  if (result.ok) return rendered;
  const detail = result.detail ? ` — ${result.detail}` : '';
  return `refused: ${result.refusal}${detail}\n\n${rendered}`;
}
